import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import {
    ListReelsQuery,
    Reel,
    ReelExportQueued,
    ReelExportQueueItem,
    ReelStatus,
    ReelUpsertPayload,
    ReelVersionExportStatus,
    ReelVersionSnapshot,
    ReelVersionsResponse,
} from './ReelMaker.types';

const LOG_PREFIX = 'ReelMaker.api';

/** Beam on Screen backend prefix on st-ck-server. */
export const REEL_MAKER_API_BASE_PATH = '/bos';

const base = (basePath?: string) => (basePath || REEL_MAKER_API_BASE_PATH).replace(/\/+$/, '');

const buildQuery = (query: ListReelsQuery = {}): string => {
    const params = new URLSearchParams();
    if (query.status && query.status !== 'all') params.set('status', query.status);
    if (query.search?.trim()) params.set('search', query.search.trim());
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortOrder) params.set('sortOrder', query.sortOrder);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
};

export const createReelMakerApi = (basePath?: string) => {
    const p = base(basePath);

    const listReels = async (query: ListReelsQuery = {}): Promise<Reel[]> => {
        try {
            const response = await apiClient.get<Reel[]>(`${p}/reels${buildQuery(query)}`);
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            Logger.error(LOG_PREFIX, `listReels failed: ${error}`);
            throw error;
        }
    };

    const getReel = async (reelId: string): Promise<Reel> => {
        try {
            const response = await apiClient.get<Reel>(`${p}/reels/${encodeURIComponent(reelId)}`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `getReel(${reelId}) failed: ${error}`);
            throw error;
        }
    };

    const createReel = async (payload: ReelUpsertPayload): Promise<Reel> => {
        try {
            const response = await apiClient.post<Reel>(`${p}/reels`, payload);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `createReel failed: ${error}`);
            throw error;
        }
    };

    const updateReel = async (reelId: string, payload: Partial<ReelUpsertPayload> & { canvas?: unknown }): Promise<Reel> => {
        try {
            const response = await apiClient.put<Reel>(`${p}/reels/${encodeURIComponent(reelId)}`, payload);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `updateReel(${reelId}) failed: ${error}`);
            throw error;
        }
    };

    const setReelStatus = async (reelId: string, status: ReelStatus): Promise<Reel> => {
        try {
            const response = await apiClient.patch<Reel>(`${p}/reels/${encodeURIComponent(reelId)}/status`, { status });
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `setReelStatus(${reelId}, ${status}) failed: ${error}`);
            throw error;
        }
    };

    const deleteReel = async (reelId: string): Promise<void> => {
        try {
            await apiClient.delete(`${p}/reels/${encodeURIComponent(reelId)}`);
        } catch (error) {
            Logger.error(LOG_PREFIX, `deleteReel(${reelId}) failed: ${error}`);
            throw error;
        }
    };

    /** Published versions (newest first) + current draft descriptor. */
    const listReelVersions = async (reelId: string): Promise<ReelVersionsResponse> => {
        try {
            const response = await apiClient.get<ReelVersionsResponse>(`${p}/reels/${encodeURIComponent(reelId)}/versions`);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `listReelVersions(${reelId}) failed: ${error}`);
            throw error;
        }
    };

    /** One snapshot with canvas. `version` = number, `vN`, or `'draft'` for the working copy. */
    const getReelVersion = async (reelId: string, version: number | string): Promise<ReelVersionSnapshot> => {
        try {
            const response = await apiClient.get<ReelVersionSnapshot>(
                `${p}/reels/${encodeURIComponent(reelId)}/versions/${encodeURIComponent(String(version))}`,
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `getReelVersion(${reelId}, ${version}) failed: ${error}`);
            throw error;
        }
    };

    /**
     * Queues a video export of a PUBLISHED version. The MP4 is produced asynchronously by the reel
     * exporter service and lands in the user's `Export` folder of the asset library.
     */
    const exportReelVersion = async (reelId: string, version: number): Promise<ReelExportQueued> => {
        try {
            const response = await apiClient.post<ReelExportQueued>(
                `${p}/reels/${encodeURIComponent(reelId)}/versions/v${version}/export`,
                {},
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `exportReelVersion(${reelId}, v${version}) failed: ${error}`);
            throw error;
        }
    };

    /** Export status of one published version, including a download URL when the MP4 is in S3. */
    const getReelVersionExport = async (reelId: string, version: number): Promise<ReelVersionExportStatus> => {
        try {
            const response = await apiClient.get<ReelVersionExportStatus>(
                `${p}/reels/${encodeURIComponent(reelId)}/versions/v${version}/export`,
            );
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `getReelVersionExport(${reelId}, v${version}) failed: ${error}`);
            throw error;
        }
    };

    /** Queued / in-progress exports of a reel (from the Redis export queue). */
    const listReelExports = async (reelId: string): Promise<ReelExportQueueItem[]> => {
        try {
            const response = await apiClient.get<ReelExportQueueItem[]>(`${p}/reels/${encodeURIComponent(reelId)}/exports`);
            return response.data ?? [];
        } catch (error) {
            Logger.error(LOG_PREFIX, `listReelExports(${reelId}) failed: ${error}`);
            throw error;
        }
    };

    return {
        basePath: p,
        listReels,
        getReel,
        createReel,
        updateReel,
        setReelStatus,
        deleteReel,
        listReelVersions,
        getReelVersion,
        exportReelVersion,
        getReelVersionExport,
        listReelExports,
    };
};

export type ReelMakerApi = ReturnType<typeof createReelMakerApi>;

export const reelMakerApi = createReelMakerApi(REEL_MAKER_API_BASE_PATH);

export const { listReels, getReel, createReel, updateReel, setReelStatus, deleteReel, listReelVersions, getReelVersion, exportReelVersion, listReelExports } = reelMakerApi;
